import { RecordFilter } from '@/types/api';
import { Button, MenuItem, Stack, Typography } from '@mui/material';
import CommentIcon from '@/public/icons/comment.svg';
import {
  BoardDetailMenu,
  BoardDetailSubText,
} from '@/components/Board/BoardDetail/styles';
import React, { forwardRef } from 'react';
import ReadMoreIcon from '@/public/icons/read_more.svg';
import { ActionType } from '@/constant/button';
import useLayoutSize from '@/hooks/useLayoutSize';
import ModifyLayout from '@/components/Record/ModifyLayout';
import { useFormContext } from 'react-hook-form';
import {
  Container,
  Divider,
  Layout,
  StyledCreate,
  StyledInput,
  StyledSubject,
  StyledSubjectContainer,
  SubHeaderLayout,
  TranslationStatusBarSlot,
} from './styles';
import useAi from '@/components/Record/hooks/useAi';
import { deleteTalkFlowDocument } from '@/hooks/apiHooks';
import ShareButton from '@/components/Record/ShareButton';
import { useRouter } from 'next/router';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { copyClipBoardText } from '@/utils';
import process from 'process';
import { useTranslation } from 'react-i18next';
import TitleXIcon from '@/public/icons/record/titleX.svg';
import {
  FavoriteChecked,
  WorkFavoriteDefault,
} from '@/theme/overrides/CustomIcons';
import useDocumentsData from '@/components/Approval/Content/hooks/useDocumentsData';
import { useRecorder } from '@/components/RecorderProvider';
import CustomVoiceVisualizer from '@/components/Record/VoiceRecord2/CustomVoiceVisualizer';
import {
  SpinnerLayout,
  SpinningIcon,
} from '@/components/Record/VoiceRecord2/styles';
import AudioPlaceholder from '@/components/Record/AudioPlaceholder';
import { formatAudioTime2 } from '@/utils/function';
import StopIcon from '@/public/icons/record/stop.svg';
import styled from '@emotion/styled';
import { CollapsibleLayoutProps } from '@/components/Document/CollapsibleLayout';
import TranslationLanguageButton from '@/components/Record/TalkFLow/TranslationLanguageButton';
import TranslationStatusBar from '@/components/Record/TalkFLow/TranslationStatusBar';
import useRecordTranslationView from '@/components/Record/TalkFLow/hooks/useRecordTranslationView';
import type { AppLanguageCode } from '@/utils/language';

type P = MiniRecordType & {
  commentCount: number;
  handleClick?(): void;
  isScrollDown?: boolean;
  suggestedSubject?: string | null;
  /** 제목줄 번역 버튼에서 도착 언어를 고른 순간 호출 (문서 전체 번역 트리거) */
  onRequestTranslation?: (language: AppLanguageCode) => void;
  /** 진행 중 번역 중단 (상태 띠의 취소) */
  onCancelTranslation?: () => void;
  /** 폼 hydration 완료 여부 — 그전에는 번역이 무시되므로 버튼을 잠근다 */
  isTranslationReady?: boolean;
} & Pick<CollapsibleLayoutProps, 'layoutType'>;

const Header = forwardRef<HTMLDivElement, P>(
  (
    {
      commentCount,
      handleClick,
      isScrollDown,
      docId,
      layoutType,
      suggestedSubject,
      onRequestTranslation,
      onCancelTranslation,
      isTranslationReady = true,
    }: P,
    ref,
  ) => {
    const router = useRouter();
    const isLinkMode = router.pathname.includes('/talkflow');
    const dialog = useDialog();
    const toast = useSnackbar();
    const { t } = useTranslation();

    const { watch, setValue, getValues } = useFormContext<RecordFilter>();
    const isBookmarked = watch('isBookmarked');

    const { isOwner } = useAi();
    const createdBy = watch('createdBy');
    const createdAt = watch('createAt');

    const { isMobile } = useLayoutSize();
    const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
      null,
    );
    const isMenuOpen = Boolean(menuAnchorEl);
    const closeMenu = () => setMenuAnchorEl(null);

    const clickMenu = async (action: ActionType) => {
      setMenuAnchorEl(null);
      const docId = getValues('docId');

      if (action === ActionType.DELETE) {
        dialog.show({
          message: t('해당 문서를 삭제하시겠어요?'),
          cancelButtonText: t('취소'),
          confirmButtonText: t('삭제'),
          onConfirm: async () => {
            try {
              await deleteTalkFlowDocument(docId);
              handleClick?.();
              toast.show({
                severity: 'success',
                message: t('문서가 삭제되었습니다.'),
                duration: 3000,
              });
            } catch (e) {
              console.log(e);
              toast.showError({ message: t('문서 삭제에 실패했습니다.') });
            }
          },
        });
      } else if (action === ActionType.LINK_COPY) {
        const resultCopy = await copyClipBoardText(
          `${process.env.NEXT_PUBLIC_ORIGIN_URL}/talkflow/${docId}`,
        );

        if (resultCopy) {
          toast.show({
            severity: 'success',
            message: t('문서 링크가 복사되었습니다.'),
            duration: 3000,
          });
        }
      }
    };

    const subject = watch('subject');
    const normalizedSubject = subject?.trim();
    const normalizedSuggestedSubject = suggestedSubject?.trim();
    const haveSubject = Boolean(normalizedSubject);
    const originalDisplaySubject =
      normalizedSubject || normalizedSuggestedSubject || undefined;
    // 편집 잠금은 hasCurrentResult 기준 — '원문 보기' 로 잠깐 돌려봤을 때도
    // 편집기로 진입시키지 않는다(다시 '번역 보기' 로 오갈 때 상태가 꼬인다).
    const {
      hasCurrentResult: isTranslationActive,
      isBannerVisible,
      pick,
    } = useRecordTranslationView(docId);
    const displaySubject = originalDisplaySubject
      ? pick('subject', originalDisplaySubject)
      : undefined;
    const [subjectDraft, setSubjectDraft] = React.useState('');
    const suggestedSubjectAtEditStart = React.useRef<string | null>(null);

    const prepareSubjectForEdit = () => {
      if (!isOwner) return;

      suggestedSubjectAtEditStart.current =
        !normalizedSubject && normalizedSuggestedSubject
          ? normalizedSuggestedSubject
          : null;
      setSubjectDraft(
        normalizedSubject ? subject : normalizedSuggestedSubject ?? '',
      );
    };

    const changeSubject = (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextSubject = event.target.value;

      setSubjectDraft(nextSubject);
      setValue(
        'subject',
        nextSubject === suggestedSubjectAtEditStart.current ? '' : nextSubject,
      );
    };

    const deleteSubject = () => {
      setSubjectDraft('');
      setValue('subject', '');
    };

    const { handleBookmark, disabled } = useDocumentsData();

    const handleBookmarked = async () => {
      try {
        const docId = getValues('docId');
        await handleBookmark(docId, isBookmarked);
        setValue('isBookmarked', !isBookmarked);
      } catch (e) {
        console.error(e);
      }
    };
    const { state } = useRecorder();

    return (
      <Container ref={ref} isLinkMode={isLinkMode}>
        {/* 번역 상태 띠는 헤더 '안'에 둔다 — Container 가 position:fixed 라
            바깥(형제)에 두면 모달에서 헤더에 가려 보이지 않는다. */}
        {onRequestTranslation && isBannerVisible && (
          <TranslationStatusBarSlot isModal={layoutType === 'modal'}>
            <TranslationStatusBar
              docId={docId}
              onCancel={onCancelTranslation}
              onRetranslate={onRequestTranslation}
            />
          </TranslationStatusBarSlot>
        )}
        <Stack
          direction="row"
          sx={{
            marginRight: layoutType === 'modal' ? `${56 + 8}px` : 0,
            minWidth: '290px',
            boxSizing: 'border-box',
            gap: '8px',
            padding: '2px 4px 2px 0px',
          }}
        >
          <ModifyLayout
            /* 번역 표시 중에는 제목 편집기로 진입시키지 않는다.
               편집기는 원문을 보여주므로 화면의 번역 제목과 어긋나고,
               번역본이 subject 로 저장될 여지도 남기지 않기 위함. */
            isOwner={isOwner && !isTranslationActive}
            style={{
              minWidth: 0,
              flex: 1,
            }}
            modifyComponent={
              <StyledSubjectContainer>
                <StyledInput
                  name="subject"
                  type="text"
                  placeholder={
                    normalizedSuggestedSubject ||
                    t('제목을 입력해 주세요 (최대 100자)')
                  }
                  autoFocus={true}
                  maxLength={100}
                  value={subjectDraft}
                  onChange={changeSubject}
                />
                {haveSubject && (
                  <TitleXIcon
                    onClick={deleteSubject}
                    style={{ margin: '0 12px' }}
                  />
                )}
              </StyledSubjectContainer>
            }
          >
            <StyledSubject
              isPlaceHolder={!displaySubject}
              isOwner={isOwner && !isTranslationActive}
              onClick={prepareSubjectForEdit}
            >
              {displaySubject ||
                (isOwner
                  ? t('제목을 입력해 주세요 (최대 100자)')
                  : t('(제목 없는 문서)'))}
            </StyledSubject>
          </ModifyLayout>
          <MiniRecord docId={docId} />
        </Stack>
        <Layout isScrollDown={isScrollDown}>
          <SubHeaderLayout isMobile={isMobile}>
            <div>
              {(createdBy?.name ?? '') + ' ' + (createdBy?.positionName ?? '')}
              <span style={{ marginLeft: '8px' }}>
                {createdBy?.departmentName ?? ''}
              </span>
            </div>
            {!isMobile && <Divider />}
            <StyledCreate>{createdAt ?? ''}</StyledCreate>
          </SubHeaderLayout>
          <div>
            <Stack direction={'row'} alignItems={'center'} spacing={'8px'}>
              <ShareButton />
              {onRequestTranslation && (
                <TranslationLanguageButton
                  docId={docId}
                  disabled={!isTranslationReady}
                  onSelect={onRequestTranslation}
                />
              )}
              <Button
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '2px',
                  padding: '0 4px 0 0',
                  '&:hover': { backgroundColor: 'var(--dk-bg-muted, #F3F5F6)' },
                }}
              >
                <CommentIcon width={'24px'} height={'24px'} />
                {/* 아이콘(stroke #888D96)·작성일(StyledCreate)과 동일한 회색으로 통일 */}
                <BoardDetailSubText
                  fontSize={isMobile ? '12px' : '14px'}
                  color={'var(--dk-text-secondary, #888D96)'}
                >
                  {commentCount.toLocaleString()}
                </BoardDetailSubText>
              </Button>
              <Button
                sx={{
                  padding: '3px',
                  '&:hover': { backgroundColor: 'var(--dk-bg-muted, #F3F5F6)' },
                }}
                onClick={handleBookmarked}
                disabled={disabled}
              >
                {isBookmarked ? (
                  <FavoriteChecked width={18} height={18} />
                ) : (
                  <WorkFavoriteDefault />
                )}
              </Button>

              <Button
                id={'menu-button'}
                aria-controls={isMenuOpen ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isMenuOpen ? 'true' : undefined}
                disabled={false}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  setMenuAnchorEl(event.currentTarget);
                }}
                sx={{ padding: '3px', minWidth: '24px' }}
              >
                <ReadMoreIcon />
              </Button>
              <BoardDetailMenu
                id={'menu'}
                anchorEl={menuAnchorEl}
                open={isMenuOpen}
                onClose={closeMenu}
                MenuListProps={{ 'aria-labelledby': 'menu-button' }}
              >
                <MenuItem
                  data-menu-type={ActionType.LINK_COPY}
                  onClick={() => clickMenu(ActionType.LINK_COPY)}
                >
                  {t('링크 복사')}
                </MenuItem>
                {isOwner && (
                  <MenuItem
                    data-menu-type={ActionType.DELETE}
                    onClick={() => clickMenu(ActionType.DELETE)}
                    sx={{ display: isOwner ? undefined : 'none' }}
                    disabled={state.docId === docId}
                  >
                    {t('삭제')}
                  </MenuItem>
                )}
              </BoardDetailMenu>
            </Stack>
          </div>
        </Layout>
      </Container>
    );
  },
);
Header.displayName = 'Header';

export default Header;

type MiniRecordType = { docId: string };
const MiniRecord = ({ docId }: MiniRecordType) => {
  const {
    controls,
    state,
    stop,
    isRecordingInProgress,
    isPausedRecording,
    recordingTime,
  } = useRecorder();

  if ((!state.isUploading && !isRecordingInProgress) || docId !== state.docId) {
    return null;
  }

  return (
    <Stack
      direction={'row'}
      sx={{
        height: '24px',
        boxSizing: 'borderBox',
        padding: '2px 8px',
        alignItems: 'center',
        gap: '5.333px',
        borderRadius: '66px',
        background: 'var(--dk-purple-soft, var(--ai-tool-portal-ai-solid-12, #F8EFF8))',
      }}
    >
      {state.isUploading ? (
        <SpinnerLayout>
          <SpinningIcon />
        </SpinnerLayout>
      ) : (
        <>
          <SvgBoxButton disabled={true}>
            {isPausedRecording ? (
              <AudioPlaceholder />
            ) : (
              <CustomVoiceVisualizer
                controls={controls}
                width={16}
                height={20}
              />
            )}
          </SvgBoxButton>
          <Typography
            sx={{
              color: '#c77ac4',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '20px',
            }}
          >
            {formatAudioTime2(recordingTime)}
          </Typography>
          <SvgBoxButton onClick={stop}>
            <StopIcon />
          </SvgBoxButton>
        </>
      )}
    </Stack>
  );
};

const SvgBoxButton = styled.button`
  display: flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border-radius: 2.67px;
  font-size: 12px;
  word-break: keep-all;
  padding: 0;

  :hover {
    background: rgba(199, 122, 196, 0.08);
  }
  :active {
    background: rgba(199, 122, 196, 0.16);
  }
  :disabled {
    background: none;
    cursor: default;
  }
`;
